import React, {Component} from 'react';
import PropTypes from 'prop-types';

import {withStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import CircularProgress from '@material-ui/core/CircularProgress';
import SvgNotFound from '@material-ui/icons/Block';

import {helper, http, api} from '../../common/common';
import SelectRegion from '../../controls/SelectRegion';
import Sort from './Sort'
import Card from './Card'

const styles = (theme) => {
    return {
        panel: {
            top: 24,
            bottom: 24,
            left: 'calc(50% - 400px)',
            width: 800,
            position: 'absolute',
            overflow: 'hidden',
        },
        header: {
            position: 'relative',
            width: '100%',
        },
        headerDivider: {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 1,
        },
        dropDownContainer: {
            top: 0,
            left: 16,
            height: 48,
            lineHeight: '48px',
            textAlign: 'left',
            position: 'relative',
        },
        sortByDateContainer: {
            top: 0,
            right: 16,
            height: 48,
            lineHeight: '48px',
            textAlign: 'left',
            position: 'absolute',
        },
        infoLabel: {
            fontStyle: 'italic',
            paddingLeft: 16,
            paddingBottom: 4,
            color: theme.palette.text.disabled,
        },
        previewPanel: {
            position: 'relative',
            width: '100%',
            height: 'calc(100% - 48px)',
            overflow: 'auto',
        },
        spinnerContainer: {
            position: 'absolute',
            top: 'calc(50% - 60px)',
            left: 'calc(50% - 80px)',
            width: 160,
            height: 120,
            textAlign: 'center',
        },
        spinner: {
            color: theme.palette.text.disabled,
        },
        spinnerLabel: {
            marginTop: 16,
            color: theme.palette.text.disabled,
        },
        notFoundContainer: {
            position: 'absolute',
            top: 'calc(50% - 60px)',
            left: 'calc(50% - 80px)',
            width: 160,
            height: 120,
            textAlign: 'center',
        },
        notFound: {
            margin: 'auto',
            width: 60,
            height: 60,
            color: theme.palette.text.disabled,
        },
        notFoundLabel: {
            marginTop: 16,
            color: theme.palette.text.disabled,
        },
    };
}

class View extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selection: 0,
            loading: false,
            dateSortMode: 'descending',
        };

        this._cancelToken = http.CancelToken.source();
        this._cards = [];
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        return (this.props.regionListTimestamp !== nextProps.regionListTimestamp ||
            this.state.selection !== nextState.selection ||
            this.state.loading !== nextState.loading ||
            this.state.dateSortMode !== nextState.dateSortMode);
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (this.props.regionListTimestamp !== prevProps.regionListTimestamp) {
            if (this.props.regionList.length > 0) {
                this._requestReload();
            }
        }
    }

    componentWillUnmount = () => {
        if (this._cancelToken) {
            this._cancelToken.cancel();
        }
    }

    render = () => {
        const {classes} = this.props;

        return (
            <Paper
                className={classes.panel}
                elevation={1}
            >
                <div className={classes.header}>
                    <SelectRegion
                        className={classes.dropDownContainer}
                        regionListTimestamp={this.props.regionListTimestamp}
                        regionList={this.props.regionList}
                        regionIndex={this.state.selection}
                        disabled={this.state.loading}
                        onSelect={this._handleSelection}
                        onError={this.props.onError}
                    />
                    <Sort
                        className={classes.sortByDateContainer}
                        label='Date'
                        value={this.state.dateSortMode}
                        onClick={this._handleChangeSortOrder}
                    />
                    <Typography
                        className={classes.infoLabel}
                        align='left'
                        variant='caption'
                    >
                        Note: The download will open a separate window which might be blocked by the browser
                    </Typography>
                    <Divider className={classes.headerDivider}/>
                </div>
                {this._renderPanel()}
            </Paper>
        );
    }

    _renderPanel = () => {
        const {classes} = this.props;

        if (this.props.regionListTimestamp === 0) {
            return this._renderLoadingSpinner('Loading regions...');
        } else if (this.state.loading) {
            return this._renderLoadingSpinner('Loading reports...');
        }

        const cards = this._generateCards();

        if (cards.length === 0) {
            return this._renderNotFound();
        }

        return (
            <div className={classes.previewPanel}>
                {cards}
            </div>
        );
    }

    _renderLoadingSpinner = (text) => {
        const {classes} = this.props;

        return (
            <div className={classes.previewPanel}>
                <div className={classes.spinnerContainer}>
                    <CircularProgress
                        className={classes.spinner}
                        size={60}
                    />
                    <Typography
                        className={classes.spinnerLabel}
                        align='center'
                        variant='button'
                    >
                        {text}
                    </Typography>
                </div>
            </div>
        );
    }

    _renderNotFound = () => {
        const {classes} = this.props;

        return (
            <div className={classes.previewPanel}>
                <div className={classes.notFoundContainer}>
                    <SvgNotFound className={classes.notFound}/>
                    <Typography
                        className={classes.notFoundLabel}
                        align='center'
                        variant='button'
                    >
                        No reports found
                    </Typography>
                </div>
            </div>
        );
    }

    _generateCards = () => {
        if (!this._cards) {
            return [];
        }

        const cards = [];

        for (const c of this._cards) {
            cards.push(<Card
                key={c.indexKey}
                onDownload={this._handleDownload}
                info={c}
            />);
        };

        return cards;
    }

    _handleSelection = (value) => {
        this.setState({
            selection: value,
            loading: true,
        });

        this._cards = [];

        this._requestEntryList(value);

        if (this.props.onChangeRegion) {
            this.props.onChangeRegion(value);
        }
    }

    _handleDownload = (info) => {
        this._requestDownload(info);
    }

    _handleChangeSortOrder = (value) => {
        if (value === 'ascending') {
            this._cards.sort(this._sortByDateAscending);
        } else {
            this._cards.sort(this._sortByDateDescending);
        }

        this.setState({
            dateSortMode: value,
        });
    }

    _signalDone = () => {
        this.setState({
            loading: false,
        });
    }

    _sortByDateDescending = (left, right) => {
        if (left.date > right.date) {
            return -1;
        } else if (left.date < right.date) {
            return 1;
        }

        return 0;
    }

    _sortByDateAscending = (left, right) => {
        if (left.date > right.date) {
            return 1;
        } else if (left.date < right.date) {
            return -1;
        }

        return 0;
    }

    _getSaveName = (info) => {
        if (info.saveName) {
            return info.saveName;
        }

        info.saveName = info.name.replace(' ', '_') + '_' + info.date + '.' + info.file_type;

        return info.saveName;
    }

    _processError = (error) => {
        this._signalDone();

        if (error.response && error.response.status === 404) {
            return;
        }

        if (this.props.onError) {
            this.props.onError(error);
        }
    }

    _requestReload = async () => {
        const selectionIndex = (this.props.regionDefaultIndex) ? this.props.regionDefaultIndex : 0;

        this.setState({
            selection: selectionIndex,
            loading: true,
        });

        this._requestEntryList(selectionIndex);
    }

    _requestEntryList = async (selectionIndex) => {
        try {
            this._cards = [];

            const region = this.props.regionList[selectionIndex];

            const response = await http.get(api.report() + '/list', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'organization': region.properties.organization,
                    'region': region.properties.key,
                },
                cancelToken: this._cancelToken.token,
            });

            this._cards = response.data.report;

            for (var i=0; i<this._cards.length; ++i) {
                this._cards[i].indexKey = i;
            }

            if (this.state.dateSortMode === 'ascending') {
                this._cards.sort(this._sortByDateAscending);
            } else {
                this._cards.sort(this._sortByDateDescending);
            }

            this._signalDone();
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._signalDone();
        }
    }

    _requestDownload = async (info) => {
        try {
            const region = this.props.regionList[this.state.selection];

            const response = await http.get(api.report() + '/url', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'organization': region.properties.organization,
                    'region': region.properties.key,
                    'report_key': info.indexKey,
                },
                cancelToken: this._cancelToken.token,
            });

            const link = document.createElement('a');
            link.id = this._getSaveName(info);
            link.target = '_blank';
            link.href = response.data.url;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            const e = helper.sanitizeError(error, 'Property Error', 'Unable to download report');
            this._processError(e);
        }
    }
}

View.propTypes = {
    getAccessCode: PropTypes.func.isRequired,
    regionListTimestamp: PropTypes.number.isRequired,
    regionList: PropTypes.arrayOf(PropTypes.object).isRequired,
    regionDefaultIndex: PropTypes.number,
    onChangeRegion: PropTypes.func,
    onError: PropTypes.func,
}

export default withStyles(styles)(View);
